import React, { useEffect, useRef, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ApuracoesListagem from "./components/ApuracoesListagem";
import { listarApuracoes, processarSumarizacaoApuracao } from "../../services/apuracao.service";
import ModalNovaApuracao from "./ModalNovaApuracao";
import ModalReprocessarAnos from "./ModalReprocessarAnos";
import ModalStatusDetalhe from "./components/ModalStatusDetalhe";
import { fetchTiposApuracao } from "./tiposApuracao.util";
import { normCnpj } from "./utils";
import { podeAbrirDetalheSumarizacao } from "./Detalhe/utils/agregarPorConta.util";

const Apuracoes = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [filtroEmpresa, setFiltroEmpresa] = useState("");
  const [filtroCnpj, setFiltroCnpj] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);
  const [tiposApuracao, setTiposApuracao] = useState([]);
  const [loadingTiposApuracao, setLoadingTiposApuracao] = useState(false);

  const [modalNova, setModalNova] = useState(false);
  const [rowAdicionar, setRowAdicionar] = useState(null);
  const [rowStatusDetalhe, setRowStatusDetalhe] = useState(null);
  const [rowReprocessar, setRowReprocessar] = useState(null);
  const [processandoSumarizacaoId, setProcessandoSumarizacaoId] = useState(null);

  const [snackbar, setSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const initialLoadDoneRef = useRef(false);

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbar(true);
  };

  useEffect(() => {
    let cancelado = false;
    (async () => {
      setLoadingTiposApuracao(true);
      try {
        const lista = await fetchTiposApuracao();
        if (!cancelado) setTiposApuracao(lista);
      } catch {
        if (!cancelado) setTiposApuracao([]);
      } finally {
        if (!cancelado) setLoadingTiposApuracao(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  useEffect(() => {
    let cancelado = false;
    const run = async () => {
      setLoading(true);
      try {
        const params = {
          page: String(page),
          limit: String(limit),
        };
        if (filtroEmpresa.trim()) params.q_empresa = filtroEmpresa.trim();
        const cnpjDigits = normCnpj(filtroCnpj);
        if (cnpjDigits) params.cnpj = cnpjDigits;
        if (filtroTipo) params.tipo = filtroTipo;
        if (filtroStatus) params.status = filtroStatus;

        const res = await listarApuracoes(params);
        if (cancelado) return;

        if (res?.sucesso) {
          const raw = Array.isArray(res.itens) ? res.itens : [];
          setRows(raw);
          setTotal(typeof res.total === "number" ? res.total : 0);
          initialLoadDoneRef.current = true;
        } else {
          setRows([]);
          setTotal(0);
          showSnackbar(res?.mensagem || "Não foi possível carregar as apurações.", "error");
        }
      } catch (e) {
        if (cancelado) return;
        showSnackbar(e?.message || "Erro ao consultar apurações.", "error");
        setRows([]);
        setTotal(0);
      } finally {
        if (!cancelado) setLoading(false);
      }
    };
    run();
    return () => {
      cancelado = true;
    };
  }, [page, limit, filtroEmpresa, filtroCnpj, filtroTipo, filtroStatus, refreshTick]);

  const resetPage = () => setPage(1);

  const handleDetalhe = (row) => {
    if (!podeAbrirDetalheSumarizacao(row)) return;
    const sufixo =
      row.tipo === "ICMS_ST_CAT42" ? "/cat42" : row.tipo === "ICMS_APURACAO" ? "/icms" : "";
    navigate(`/relatorios/apuracoes/${row.id}/detalhe${sufixo}`);
  };

  const handleProcessamento = (row) => {
    setRowStatusDetalhe(row);
  };

  const handleReprocessar = (row) => {
    setRowReprocessar(row);
  };

  const recarregarListagem = () => setRefreshTick((t) => t + 1);

  const handleVerProcessamentoCompleto = () => {
    if (!rowStatusDetalhe) return;
    setRowStatusDetalhe(null);
    navigate(
      `/relatorios/processamento?apuracao_id=${encodeURIComponent(rowStatusDetalhe.id)}&cnpj=${encodeURIComponent(rowStatusDetalhe.cnpj)}`
    );
  };

  const handleProcessarSumarizacao = async (row) => {
    if (!row?.id || processandoSumarizacaoId) return;
    setProcessandoSumarizacaoId(row.id);
    try {
      const res = await processarSumarizacaoApuracao(row.id);
      if (res?.sucesso) {
        showSnackbar(
          res.mensagem || "Sumarização enfileirada para processamento.",
          "success"
        );
        recarregarListagem();
      } else {
        showSnackbar(
          res?.mensagem || "Não foi possível processar a sumarização.",
          "error"
        );
      }
    } catch (e) {
      showSnackbar(e?.message || "Erro ao solicitar processamento da sumarização.", "error");
    } finally {
      setProcessandoSumarizacaoId(null);
    }
  };

  return (
    <>
      <ApuracoesListagem
        loading={loading && !initialLoadDoneRef.current}
        loadingTable={loading && initialLoadDoneRef.current}
        rows={rows}
        total={total}
        page={page}
        limit={limit}
        onPageChange={setPage}
        onLimitChange={(newLimit) => {
          setLimit(newLimit);
          setPage(1);
        }}
        filtroEmpresa={filtroEmpresa}
        onFiltroEmpresaChange={(value) => {
          setFiltroEmpresa(value);
          resetPage();
        }}
        filtroCnpj={filtroCnpj}
        onFiltroCnpjChange={(value) => {
          setFiltroCnpj(value);
          resetPage();
        }}
        filtroTipo={filtroTipo}
        onFiltroTipoChange={(value) => {
          setFiltroTipo(value);
          resetPage();
        }}
        filtroStatus={filtroStatus}
        onFiltroStatusChange={(value) => {
          setFiltroStatus(value);
          resetPage();
        }}
        onRefresh={recarregarListagem}
        onDetalhe={handleDetalhe}
        onAdicionar={setRowAdicionar}
        onProcessamento={handleProcessamento}
        onProcessarSumarizacao={handleProcessarSumarizacao}
        onReprocessar={handleReprocessar}
        processandoSumarizacaoId={processandoSumarizacaoId}
        onNovaApuracao={() => setModalNova(true)}
        tiposApuracao={tiposApuracao}
        loadingTiposApuracao={loadingTiposApuracao}
      />

      <ModalNovaApuracao
        open={modalNova || Boolean(rowAdicionar)}
        rowContext={rowAdicionar}
        tiposApuracao={tiposApuracao}
        loadingTiposApuracao={loadingTiposApuracao}
        onClose={() => {
          setModalNova(false);
          setRowAdicionar(null);
        }}
        onSuccess={(msg) => {
          showSnackbar(msg, "success");
          setModalNova(false);
          setRowAdicionar(null);
          recarregarListagem();
        }}
        onError={(msg) => showSnackbar(msg, "error")}
        onClearFeedback={() => setSnackbar(false)}
      />

      <ModalStatusDetalhe
        open={Boolean(rowStatusDetalhe)}
        row={rowStatusDetalhe}
        onClose={() => setRowStatusDetalhe(null)}
        onVerProcessamento={handleVerProcessamentoCompleto}
      />

      <ModalReprocessarAnos
        open={Boolean(rowReprocessar)}
        idApuracao={rowReprocessar?.id}
        apuracao={rowReprocessar}
        onClose={() => setRowReprocessar(null)}
        onSuccess={(msg) => {
          showSnackbar(msg || "Reprocessamento enfileirado.", "success");
          setRowReprocessar(null);
          recarregarListagem();
        }}
        onError={(msg) => showSnackbar(msg, "error")}
      />

      <Snackbar
        open={snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setSnackbar(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: "100%",
            fontWeight: "bold",
            fontSize: "1rem",
            "& .MuiAlert-icon": { fontSize: "1.5rem" },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Apuracoes;
